import { useState } from "react";
import moment from "moment";
import { validateFields, setCutOffTime } from '../../utils';

/**
 * Custom hook for managing auto-fill functionality for VTO offers
 * Handles auto-fill state, validation, and offer creation/updating
 */
const useAutoFill = ({
    offersConsolidation,
    setOffersConsolidation,
    setOfferDetails,
    activeKey,
    setActiveKey,
    selectedDates,
    selectedStation,
    selectedDepartment,
    selectedClassification,
    selectedDutyAssig,
    isCheckedApplyAll,
    stationTime,
    setShowRequiredFieldsError,
    setShowCDAError,
    setErrorMessage,
    setShowError,
    onSuccess
}) => {
    const [showAutoFillPopup, setShowAutoFillPopup] = useState(false);
    const [pendingAutoFillData, setPendingAutoFillData] = useState(null);

    // Helper function to check if an offer is empty (no time data entered)
    const isOfferEmpty = (offer) => {
        return !offer.offerStartTimeHr && !offer.offerStartTimeMin && 
               !offer.offerEndTimeHr && !offer.offerEndTimeMin;
    };

    // Helper function to calculate duration from start and end times (handles overnight)
    const calculateDuration = (startTime, endTime) => {
        const [startHr, startMin] = startTime.split(':');
        const [endHr, endMin] = endTime.split(':');

        const startMinutes = parseInt(startHr) * 60 + parseInt(startMin);
        const endMinutes = parseInt(endHr) * 60 + parseInt(endMin);
        let diffMinutes = endMinutes - startMinutes;
        if (diffMinutes < 0) {
            diffMinutes += 24 * 60; // overnight: wrap around 24 hours
        }
        const durationHours = Math.floor(diffMinutes / 60);
        const durationMins = diffMinutes % 60;
        
        return `${String(durationHours).padStart(2, '0')}:${String(durationMins).padStart(2, '0')}`;
    };

    // Helper function to fill an existing offer with autofill data
    const fillExistingOffer = (offerId, offerData) => {
        const [startHr, startMin] = offerData.startTime.split(':');
        const [endHr, endMin] = offerData.endTime.split(':');
        const duration = calculateDuration(offerData.startTime, offerData.endTime);

        let previousOffers = { ...offersConsolidation };
        let currentOffer = { ...previousOffers[offerId] };
        
        currentOffer.offerStartTimeHr = startHr;
        currentOffer.offerStartTimeMin = startMin;
        currentOffer.offerEndTimeHr = endHr;
        currentOffer.offerEndTimeMin = endMin;
        currentOffer.duration = duration;
        currentOffer.slots = offerData.vtoOp || 1;
        currentOffer.classification = isCheckedApplyAll ? selectedClassification : currentOffer.classification;
        currentOffer.dutyAssignment = isCheckedApplyAll ? selectedDutyAssig : currentOffer.dutyAssignment;
        currentOffer.error = false;

        // Detect overnight offer
        if (parseInt(startHr) > parseInt(endHr) || (startHr === endHr && parseInt(startMin) > parseInt(endMin))) {
            currentOffer.note = "NOTE: *This offer will end the following day";
        } else {
            currentOffer.note = "";
        }

        previousOffers[offerId] = currentOffer;
        setOfferDetails(currentOffer);
        setOffersConsolidation(previousOffers);
        
        // Keep accordion open if user needs to add classification/duty assignment manually
        if (isCheckedApplyAll) {
            setActiveKey(null); // Close the form after filling
        } else {
            setActiveKey(offerId.toString()); // Keep open for manual C&DA entry
        }
    };

    // Helper function to create a new offer with autofill data
    const createNewAutoFilledOffer = (offerData) => {
        const [startHr, startMin] = offerData.startTime.split(':');
        const [endHr, endMin] = offerData.endTime.split(':');
        const duration = calculateDuration(offerData.startTime, offerData.endTime);

        let currentCount = Object.keys(offersConsolidation).length > 0 
            ? Number(Object.keys(offersConsolidation)[Object.keys(offersConsolidation).length - 1]) + 1 
            : 0;
        let currentOffers = { ...offersConsolidation };

        // Detect overnight offer
        const isOvernight = parseInt(startHr) > parseInt(endHr) || (startHr === endHr && parseInt(startMin) > parseInt(endMin));

        let newOffer = {
            "id": currentCount,
            "offerStartTimeHr": startHr,
            "offerStartTimeMin": startMin,
            "offerEndTimeHr": endHr,
            "offerEndTimeMin": endMin,
            "duration": duration,
            "slots": offerData.vtoOp || 1,
            "error": false,
            "note": isOvernight ? "NOTE: *This offer will end the following day" : "",
            "classification": isCheckedApplyAll ? selectedClassification : "",
            "dutyAssignment": isCheckedApplyAll ? selectedDutyAssig : "",
            "cdaError": false,
            "cutOffTime": selectedDates.reduce((acc, date) => {
                acc[date] = setCutOffTime(date, stationTime);
                return acc;
            }, {}),
        };

        setOfferDetails(newOffer);
        setOffersConsolidation({ ...currentOffers, [newOffer.id]: { ...newOffer } });
        
        // Keep accordion open if user needs to add classification/duty assignment manually
        if (isCheckedApplyAll) {
            setActiveKey(null); // Close the form after creating
        } else {
            setActiveKey(currentCount.toString()); // Keep open for manual C&DA entry
        }
    };

    const handleAutoFillOffer = (offerData = { startTime: "10:30", endTime: "12:30", vtoOp: 7 }) => {
        // Validate required fields first
        const { isValid } = validateFields(selectedDepartment, selectedDates, selectedStation, selectedClassification, selectedDutyAssig, isCheckedApplyAll);
        if (!isValid) {
            setShowRequiredFieldsError(true);
            setShowCDAError(true);
            return false;
        }
        setShowRequiredFieldsError(false);
        setShowCDAError(false);

        // Validate start time for today's offers (must be at least 30 minutes from now)
        const todaySelected = selectedDates.includes(moment(stationTime).format("MM/DD/YYYY"));
        if (todaySelected && stationTime) {
            const now = stationTime;
            const minStartTime = moment(now).add(30, 'minutes');
            const [startHr, startMin] = offerData.startTime.split(':');
            const startTime = moment(`${moment(now).format("MM/DD/YYYY")} ${startHr}:${startMin}`, "MM/DD/YYYY HH:mm");
            
            if (startTime.isBefore(now)) {
                setErrorMessage("Start time has already passed for today.");
                setShowError(true);
                return false;
            } else if (startTime.isBefore(minStartTime)) {
                setErrorMessage("Start time should be at least 30 minutes from now for today's offers.");
                setShowError(true);
                return false;
            }
        }

        // Check if there's an active offer
        if (activeKey !== null && offersConsolidation[activeKey]) {
            const currentOffer = offersConsolidation[activeKey];
            
            // If the current offer is empty, fill it directly
            if (isOfferEmpty(currentOffer)) {
                fillExistingOffer(activeKey, offerData);
                return true;
            }
            
            // If the current offer has data, show confirmation popup
            setPendingAutoFillData(offerData);
            setShowAutoFillPopup(true);
            return false; // Return false because the actual add happens in the popup
        }

        // No active offer exists, create a new one
        createNewAutoFilledOffer(offerData);
        return true;
    };

    const handleAutoFillOverwrite = () => {
        if (pendingAutoFillData && activeKey !== null) {
            fillExistingOffer(activeKey, pendingAutoFillData);
            // Call success callback with the offer key
            if (onSuccess && pendingAutoFillData.roleType) {
                const offerKey = `${pendingAutoFillData.roleType}-${pendingAutoFillData.startTime}-${pendingAutoFillData.endTime}`;
                onSuccess(offerKey);
            }
        }
        setShowAutoFillPopup(false);
        setPendingAutoFillData(null);
    };

    const handleAutoFillCreateNew = () => {
        if (pendingAutoFillData) {
            createNewAutoFilledOffer(pendingAutoFillData);
            // Call success callback with the offer key
            if (onSuccess && pendingAutoFillData.roleType) {
                const offerKey = `${pendingAutoFillData.roleType}-${pendingAutoFillData.startTime}-${pendingAutoFillData.endTime}`;
                onSuccess(offerKey);
            }
        }
        setShowAutoFillPopup(false);
        setPendingAutoFillData(null);
    };

    const closeAutoFillPopup = () => {
        setShowAutoFillPopup(false);
        setPendingAutoFillData(null);
    };

    return {
        // State
        showAutoFillPopup,
        pendingAutoFillData,
        
        // Functions
        handleAutoFillOffer,
        handleAutoFillOverwrite,
        handleAutoFillCreateNew,
        closeAutoFillPopup,
        isOfferEmpty,
        fillExistingOffer,
        createNewAutoFilledOffer
    };
};

export default useAutoFill;
